import { useEffect, useState } from "react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "./ui.tsx";

/**
 * 「先选一个，再写理由」的对话框。
 *
 * 🔴 **原来这两处是连着弹两个 `window.prompt`** —— 调级要手打等级标识
 *    （"填等级标识（如 gold）"），转移客户要手打组织 id
 *    （"填组织 id（组织树里能查到）"）。手打一个 id 意味着：
 *    - 他得先去另一个页面查，回来再敲；
 *    - **敲错一位就把客户转给了完全不相干的商家**，而这件事只改以后不改过去，
 *      发现时已经晚了。
 *    现在直接从现有的列表里选。
 */
export function PickDialog({
  open,
  title,
  description,
  label,
  options,
  confirmLabel = "确认",
  loading,
  onConfirm,
  onClose,
}: {
  open: boolean;
  title: string;
  description?: string;
  label: string;
  options: { value: string; label: string }[];
  confirmLabel?: string;
  loading?: boolean;
  onConfirm: (value: string, reason: string) => void;
  onClose: () => void;
}) {
  const [value, setValue] = useState("");
  const [reason, setReason] = useState("");

  /** ⚠️ 每次打开都清空 —— 留着上一次的选择，很容易把上一个人的处理按到这一个身上。 */
  useEffect(() => {
    if (open) {
      setValue("");
      setReason("");
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <span className="mb-1.5 block text-[13px] text-muted-foreground">{label}</span>
            <select
              className="h-11 w-full rounded-lg border border-border bg-background px-3 text-[15px] outline-none focus:border-brand-500"
              value={value}
              onChange={(e) => setValue(e.target.value)}
            >
              <option value="">请选择</option>
              {options.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
            {/* ⚠️ 一个都没有时要说清楚，别让他对着空下拉发呆。 */}
            {options.length === 0 && (
              <p className="mt-1.5 text-[12px] text-amber-700">
                没有可选项 —— 先去对应的页面建一个。
              </p>
            )}
          </div>

          <div>
            <span className="mb-1.5 block text-[13px] text-muted-foreground">
              理由（会记进变更日志）
            </span>
            <textarea
              rows={3}
              className="w-full resize-none rounded-lg border border-border bg-background px-3 py-2.5 text-[14px] leading-relaxed outline-none focus:border-brand-500"
              placeholder="纠纷时这是唯一依据，写清楚点"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            取消
          </Button>
          <Button
            loading={loading}
            disabled={!value || !reason.trim()}
            onClick={() => onConfirm(value, reason.trim())}
          >
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
